import { IDCustomerLookupLayout } from "../../../common/lookups/Layouts/IdLayouts";

// Codes/labels taken verbatim from the legacy flag definitions in uARM0009.dfm - the
// TAbStringField Codes.Strings / Items.Strings pairs on the ARCOLLECTIONCREDITHEADER and
// ARCOLLECTIONCREDITDETAIL queries. These are the values ARD0009 wrote for 25 years, so
// existing rows only ever hold these codes.
// qryARCollectionCreditHeaderEditTYPE: Codes T,A,C,N.
export const CollectionCreditTypeOptions = [
    { id: 'T', text: 'Transaction Approval' },
    { id: 'A', text: 'Credit Approval' },
    { id: 'C', text: 'Credit Check' },
    { id: 'N', text: 'Collection Note' },
];

// qryARCollectionCreditHeaderEditSTATUS: Codes O,C.
export const CollectionCreditStatusOptions = [
    { id: 'O', text: 'Open' },
    { id: 'C', text: 'Closed' },
];

// qryARCollectionCreditDetailCONTACTMETHOD: Codes T,P,F,E. Note 'T' is Phone and
// 'P' is Person - not the other way round.
export const ContactMethodOptions = [
    { id: 'T', text: 'Phone' },
    { id: 'P', text: 'Person' },
    { id: 'F', text: 'Fax' },
    { id: 'E', text: 'Email' },
];

// New-record defaults, from uARM0009.pas's qryARCollectionCreditHeaderEditAfterInsert
// ("TYPE" := 'N'; "STATUS" := 'O'). The detail's AfterInsert defaults dates and the
// header reference but deliberately leaves CONTACTMETHOD blank, so there is no
// DEFAULT_CONTACT_METHOD here.
export const DEFAULT_TYPE = 'N';
export const DEFAULT_STATUS = 'O';

// ARCOLLECTIONCREDITHEADER.STATUS value that moves a follow-up into the Collections
// History tab (legacy qryARCollectionCreditHistory: "... and STATUS = 'C'").
export const CLOSED_STATUS = 'C';

// ARD0009's grdOpenFollowUpTop column set, declared once as data and rendered by BOTH
// consumers - List.js's own "Open Follow-up" tab and the embeddable OpenFollowUpGrid used
// by OM's Query Orders By Customer popup - so the two grids can never drift apart.
// Same map-over-column-definitions approach as CustomerBrowseGrid in this folder.
export const OpenFollowUpColumns = [
    { dataField: 'RecordNumber', caption: 'Record #', dataType: 'string', allowEditing: false },
    { dataField: 'CustomerIdentityId', caption: 'Customer', dataType: 'string' },
    { dataField: 'CustomerName', caption: 'Name', dataType: 'string' },
    { dataField: 'Reference', caption: 'Reference', dataType: 'string' },
    {
        dataField: 'Type', caption: 'Type', dataType: 'string',
        lookup: { dataSource: CollectionCreditTypeOptions, displayExpr: 'text', valueExpr: 'id' },
    },
    {
        dataField: 'Status', caption: 'Status', dataType: 'string',
        lookup: { dataSource: CollectionCreditStatusOptions, displayExpr: 'text', valueExpr: 'id' },
    },
    { dataField: 'Description', caption: 'Description', dataType: 'string' },
    { dataField: 'InitialCallDate', caption: 'Call Date', dataType: 'datetime' },
    // Default ordering matches qryARCollectionCreditHeaderRead's
    // "OrderBy isnull(NEXTFOLLOWUPDATE,'12/01/3000') Desc" - latest follow-up dates
    // (and headers with none) at the top.
    { dataField: 'NextFollowUpDate', caption: 'Next Follow-up', dataType: 'datetime', sortOrder: 'desc', sortIndex: 0 },
    { dataField: 'RowId', caption: 'Row Id', visible: false, showInColumnChooser: false },
];

// Legacy grdOpenFollowUpTopCustomDraw: rows whose next follow-up date is due today or
// already past are drawn in the warning colours, so overdue collections work stands out.
// Shared by both grids above for the same reason as the column set.
export const highlightOverdueFollowUpRow = (e) => {
    if (e.rowType !== 'data' || !e.data?.NextFollowUpDate) return;
    const nextFollowUpDate = new Date(e.data.NextFollowUpDate);
    nextFollowUpDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (nextFollowUpDate <= today) {
        e.rowElement.style.backgroundColor = '#f8d7da';
        e.rowElement.style.color = '#842029';
    }
};

export const SearchLayout = [
    {
        "groupId": 1,
        "fields": [
            { "name": "Reference", "tableName": "ARCOLLECTIONCREDITHEADER", "label": "Reference #", "type": "text", "required": false, "visible": true, "isLabelChanged": false },
        ]
    },
    {
        "groupId": 2,
        "fields": [
            { "name": "Type", "tableName": "ARCOLLECTIONCREDITHEADER", "label": "Type", "type": "select", "required": false, "visible": true, "isLabelChanged": false },
        ]
    },
    {
        "groupId": 3,
        "fields": [
            { "name": "Status", "tableName": "ARCOLLECTIONCREDITHEADER", "label": "Status", "type": "select", "required": false, "visible": true, "isLabelChanged": false },
        ]
    }
];

// ARD0009 only enables the Customer lookup while a header is being added
// (navBottomButtonClick: ab_EnableControl(edtP_CUSTOMER, BtnPressed = abAdd)) - once a
// follow-up is saved its customer is fixed, because the detail rows' Contact With values
// are IDRELATIONSHIPLINKS rows belonging to that customer. Pass customerReadOnly=true
// for a saved header to reproduce that.
export const buildPrimaryLayout = ({ customerReadOnly = false } = {}) => [
    {
        "groupId": 1,
        "label": "",
        "fields": [
            {
                "name": "RECORD",
                "localizedField": "KEYSEQUENCE",
                "tableName": "ARCOLLECTIONCREDITHEADER",
                "label": "Record #",
                "type": "text",
                "required": false,
                "visible": true,
                "readOnly": true,
                "isLabelChanged": false,
            },
            {
                "name": "R_CUSTOMER",
                "tableName": "ARCOLLECTIONCREDITHEADER",
                "label": "Customer",
                "type": "lookup",
                "layout": IDCustomerLookupLayout(),
                "required": true,
                "visible": true,
                "readOnly": customerReadOnly,
                "isLabelChanged": false,
            },
            {
                "name": "REFERENCE",
                "tableName": "ARCOLLECTIONCREDITHEADER",
                "label": "Reference #",
                "type": "text",
                "required": false,
                "maxLength": 30,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                "name": "DESCRIPTION",
                "tableName": "ARCOLLECTIONCREDITHEADER",
                "label": "Description",
                "type": "text",
                "required": false,
                "maxLength": 40,
                "visible": true,
                "isLabelChanged": false,
            },
        ]
    },
    {
        "groupId": 2,
        "label": "",
        "fields": [
            {
                "name": "TYPE",
                "tableName": "ARCOLLECTIONCREDITHEADER",
                "label": "Type",
                "type": "select",
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                "name": "STATUS",
                "tableName": "ARCOLLECTIONCREDITHEADER",
                "label": "Status",
                "type": "select",
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                "name": "INITIALCALLDATE",
                "tableName": "ARCOLLECTIONCREDITHEADER",
                "label": "Call Date",
                "type": "datetime",
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                "name": "COMMENT",
                "tableName": "ARCOLLECTIONCREDITHEADER",
                "label": "Comment",
                "type": "textarea",
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
        ]
    }
];
